/* eslint-disable no-magic-numbers */
import { DateTime } from "luxon";
import { resolveVirtualMeetingType } from "@teaming/common-team";
import { VirtualMeetingType } from "@teaming/common-enums";

export { default as debounce } from "./debounce";
export { default as markdown } from "./markdown";
export { default as sleep } from "./sleep";
export { default as when } from "./when";
export { default as luxonext } from "./luxonext";

/**
 * Update (by comparator) and concat the difference of two collections
 * @param {Array} collection1
 * @param {Array} collection2
 * @param {Function} [comparator]
 */
export const upcat = ( collection1, collection2, comparator = ( a, b ) => a === b ) => {
	const result = [];
	// array will be mutated, so clone it first
	const otherCollection = [ ...collection2, ];

	const update = item => {
		let index = 0;
		for ( const otherItem of otherCollection ) {
			if ( comparator( item, otherItem ) ) {
				// keep the item from otherCollection,
				// disregard the item from collection1
				result.push( otherItem );
				otherCollection.splice( index, 1 );
				return;
			}
			index += 1;
		}
		// otherwise, keep the item from collection1
		result.push( item );
	};

	for ( const item of collection1 ) {
		update( item );
	}

	// pick up any remaining items and place them at the end
	return result.concat( otherCollection );
};

upcat.idComparator = ( o1, o2 ) => o1.id === o2.id;

/**
 * Walk the node tree and apply actions to nodes of specific types
 * @param {Node} head
 * @param {Array.<Node>} tail
 * @param {Object.<Function>} actions
 * @returns {Function|undefined}
 */
export const walk = ( head, tail = [], actions = {} ) => {
	const nodeAction = actions[ head.type ];
	if ( nodeAction ) {
		nodeAction( head );
	}
	// walk the head's children, if any
	if ( head.content ) {
		return () => {
			return walk( head.content[ 0 ], [
				...head.content.slice( 1 ),
				...tail,
			], actions );
		};
	}
	// there are no children, and there are no remaining nodes; exit
	if ( !tail.length ) {
		return;
	}
	// walk the remaining nodes, if any
	return () => {
		return walk( tail[ 0 ], tail.slice( 1 ), actions );
	};
};

export const isJSDate = dateLike => Object.prototype.toString.call( dateLike ) === "[object Date]";

export const isMomentDate = dateLike => !!dateLike?._isAMomentObject;

// TODO: move to luxonext.js
export const isLuxonDate = dateLike => !!dateLike?.isLuxonDateTime;

// TODO: move to luxonext.js
export const luxonify = ( dateLike, defaultToNow = true ) => {
	if ( !dateLike ) {
		if ( defaultToNow ) {
			return DateTime.now();
		}
		throw new Error( "dateLike must be an object" );
	}

	// this is a luxon object already
	if ( isLuxonDate( dateLike ) ) {
		return dateLike;
	}

	// this is a JS date
	if ( isJSDate( dateLike ) ) {
		return DateTime.fromJSDate( dateLike );
	}

	// this is a moment date
	if ( isMomentDate( dateLike ) ) {
		return DateTime.fromISO( dateLike.toISOString() );
	}

	// we don't know what this is, throw it to the wind
	try {
		return new DateTime( dateLike );
	} catch ( e ) {
		// eslint-disable-next-line no-console
		console.warn( "attempted to convert unknown date-like to Luxon object:", dateLike );
		throw e;
	}
};

export const isString = target => Object.prototype.toString.call( target ) === "[object String]";

export const isObject = target => Object.prototype.toString.call( target ) === "[object Object]";

export const isArray = target => Array.isArray( target );

export const nthify = number => {
	if ( isNaN( number ) ) {
		return "";
	}
	const numberStr = `${ number }`;
	const isTeen = ( number >= 10 && number < 20 );
	if ( !isTeen && numberStr.endsWith( "1" ) ) {
		return `${ number }st`;
	}
	if ( !isTeen && numberStr.endsWith( "2" ) ) {
		return `${ number }nd`;
	}
	if ( !isTeen && numberStr.endsWith( "3" ) ) {
		return `${ number }rd`;
	}
	return `${ number }th`;
};

/**
 * Generate the Zoom OAuth url with clientId and redirectUri
 * @param clientId String
 * @param redirectUri String
 * @param zoomOauthUrl String
 * @returns {string}
 */
export const generateZoomAuthUrl = ( { clientId, redirectUri, zoomOauthUrl, } ) => {
	const _zoomOauthUrl = new URL( zoomOauthUrl );
	_zoomOauthUrl.searchParams.set( "response_type", "code" );
	_zoomOauthUrl.searchParams.set( "client_id", clientId );
	_zoomOauthUrl.searchParams.set( "redirect_uri", redirectUri );
	return _zoomOauthUrl.toString();
};

/**
 * Check if the url is a zoom link
 * @param url String
 * @returns {boolean}
 */
export const isZoomLink = url => {
	try {
		return resolveVirtualMeetingType( url ) === VirtualMeetingType.ZOOM;
	} catch ( e ) {
		return false;
	}
};
